import { type ReactNode, useEffect, useRef } from 'react';

import { DraftRegistry } from './draft-registry';
import { DraftManagerContext } from './DraftManagerContext';
import { isStorageAvailable, LocalStorageAdapter, MemoryStorageAdapter } from './storage-adapters';
import type { StorageAdapter } from './types';
import { DEFAULT_DEBOUNCE_MS, DEFAULT_DRAFT_EXPIRY_MS, PRUNE_INTERVAL_MS } from './types';

// ─── Provider ─────────────────────────────────────────────────────────────────

interface DraftManagerProviderProps {
  children: ReactNode;
  /**
   * Storage backend for drafts.
   *
   * Defaults to `LocalStorageAdapter` when localStorage is available,
   * falling back to `MemoryStorageAdapter` otherwise (private browsing,
   * blocked storage).
   *
   * Pass a custom adapter to use sessionStorage, IndexedDB, or a server API.
   *
   * @example
   * <DraftManagerProvider adapter={new SessionStorageAdapter()}>
   */
  adapter?: StorageAdapter;
  /** Debounce delay for `saveDebounced` in ms. Default: 800 ms. */
  debounceMs?: number;
  /** Default draft TTL in ms. Default: 7 days. */
  defaultExpiryMs?: number;
}

/**
 * Provides a DraftRegistry to the React subtree.
 *
 * Mount ONCE inside ERPCoreProvider (deepest nesting — after all other
 * managers so it can safely call `flushAll()` independently).
 *
 * ## Responsibilities
 *
 * - Creates a `DraftRegistry` with the configured storage adapter.
 * - Prunes expired drafts on mount and every 10 minutes.
 * - Flushes all pending debounced saves when the browser tab is closed
 *   (`beforeunload` event) so no user work is lost.
 *
 * ## Storage adapter selection
 *
 * - `LocalStorageAdapter` (default) — persists across sessions; best for ERP
 *   workflows where users may close the browser mid-transaction.
 * - `SessionStorageAdapter` — persists for the tab session only; suitable for
 *   sensitive fields (payment details, confidential notes).
 * - `MemoryStorageAdapter` — in-process only; use for testing or when both
 *   Web Storage APIs are unavailable.
 *
 * @example
 * // Default setup (localStorage + 7-day expiry):
 * <DraftManagerProvider>
 *   <App />
 * </DraftManagerProvider>
 *
 * @example
 * // Custom adapter:
 * <DraftManagerProvider
 *   adapter={new SessionStorageAdapter('garagewale-session-drafts')}
 *   debounceMs={500}
 *   defaultExpiryMs={24 * 60 * 60 * 1000}  // 24 hours
 * >
 *   <App />
 * </DraftManagerProvider>
 */
export function DraftManagerProvider({
  children,
  adapter,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  defaultExpiryMs = DEFAULT_DRAFT_EXPIRY_MS,
}: DraftManagerProviderProps) {
  // Resolve storage adapter — prefer provided adapter, then localStorage,
  // then fall back to memory if localStorage is unavailable.
  const resolvedAdapter = useRef<StorageAdapter | null>(null);
  resolvedAdapter.current ??=
    adapter ??
    (isStorageAvailable('local') ? new LocalStorageAdapter() : new MemoryStorageAdapter());

  // Create registry once — stable across re-renders
  const registryRef = useRef<DraftRegistry | null>(null);
  registryRef.current ??= new DraftRegistry({
    store: resolvedAdapter.current,
    debounceMs,
    defaultExpiryMs,
  });
  const registry = registryRef.current;

  // ── Prune expired drafts on mount + periodic ──────────────────────────────
  useEffect(() => {
    registry.pruneExpired();

    const interval = setInterval(() => {
      registry.pruneExpired();
    }, PRUNE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [registry]);

  // ── Flush pending saves on page unload ────────────────────────────────────
  useEffect(() => {
    function handleBeforeUnload(): void {
      // Synchronously flush any pending debounced saves so user data is not
      // lost if the tab is closed mid-debounce window
      registry.flushAll();
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [registry]);

  return (
    <DraftManagerContext.Provider value={{ registry }}>{children}</DraftManagerContext.Provider>
  );
}
